import { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faList } from '@fortawesome/free-solid-svg-icons';
import {
  dashboardWidgetRegistry,
  NewsWidget,
  WidgetHostContext,
  widgetMenuEntryToItem,
} from '@/core/widgets/export';
import { useEffectivePermissions } from '@/core/permissions/export';
import { MenuListItem, MenuListPages, MobileFullscreenPanel } from '@/shared/components/export';
import '@/shared/theme/navbar-buttons.scss';

export default function MobileWidgetSelector({
  activeWidgetId,
  onBack,
  onClose,
}: {
  activeWidgetId: string | null;
  /** Returns to the hamburger menu (vs. onClose, which closes back to the dashboard). */
  onBack: () => void;
  onClose: () => void;
}) {
  const host = useContext(WidgetHostContext);
  const permissions = useEffectivePermissions();

  const visibleWidgets = dashboardWidgetRegistry
    .getAll()
    .filter(widget => !widget.shouldHideToUser || !widget.shouldHideToUser(permissions));

  const selectWidget = (id: string) => {
    host.setWidgetPath(id, '');
    onClose();
  };

  const toRow = (widget: NewsWidget): MenuListItem => ({
    key: widget.id,
    label: widget.headerPanel,
    checked: widget.id === activeWidgetId,
    onSelect: () => selectWidget(widget.id),
  });

  // Any widget can opt into a named category (e.g. "Bullets") via its
  // mobileCategory field — set centrally in dashboard-widgets.ts — rather
  // than this page hardcoding which widget ids belong together.
  const categoryOrder: string[] = [];
  const widgetsByCategory = new Map<string, NewsWidget[]>();
  visibleWidgets.forEach(widget => {
    if (!widget.mobileCategory) return;
    if (!widgetsByCategory.has(widget.mobileCategory)) {
      widgetsByCategory.set(widget.mobileCategory, []);
      categoryOrder.push(widget.mobileCategory);
    }
    widgetsByCategory.get(widget.mobileCategory)!.push(widget);
  });

  const categoryItems: MenuListItem[] = categoryOrder.map(category => ({
    key: `category-${category}`,
    label: category,
    icon: faList,
    collapsible: widgetsByCategory.get(category)!.map(toRow),
  }));

  const otherWidgets = visibleWidgets.filter(widget => !widget.mobileCategory);

  // Dynamic/URL-templated widgets (calendars, article series, podcasts, ...)
  // aren't static registry entries — same categorization the desktop "+ Add
  // widget" control builds from each factory's own browsable tree.
  const factoryCategories: MenuListItem[] = dashboardWidgetRegistry
    .getFactories()
    .filter(factory => factory.getListableUrls().length > 0)
    .map((factory, index) => ({
      key: `factory-${index}`,
      label: factory.headerPanel,
      collapsible: factory.getListableUrls().map((entry, entryIndex) =>
        widgetMenuEntryToItem(entry, `factory-${index}-${entryIndex}`, {
          onSelect: selectWidget,
          isChecked: url => url === activeWidgetId,
        }),
      ),
    }));

  const items: MenuListItem[] = [
    ...categoryItems,
    ...factoryCategories,
    ...otherWidgets.map(toRow),
  ];

  return (
    <MobileFullscreenPanel
      header={
        <button
          type="button"
          className="btn btn-link p-0 d-flex align-items-center gap-2 text-decoration-none"
          onClick={onBack}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-muted" />
          <span className="text-body">Dashboards</span>
        </button>
      }
      headerDivider={false}
      closeButtonClassName="btn btn-outline-primary-500 btn-sm navbar-btn navbar-btn-square"
      onClose={onClose}
    >
      <MenuListPages items={items} />
    </MobileFullscreenPanel>
  );
}
